import uuid from "uuid-random";
import { DialogManagerClientController } from "./dialogManagerClientController";
import { getGdDisplayIndexWithMine, getPlacementSettings, adjustPlacementBounds } from "../common/windowFunctions";
import { logStatus } from "../utils";
const legacyUINameMap = {
    fdc3Resolver: "FDC3Resolver",
    linkerWindow: "LinkerWindow",
    "Docking Move Mask": "DockingGroupMask",
    yesNo: "YesNoDialog",
    singleInput: "SingleInputDialog",
    "Process Monitor": "ProcessMonitor",
    "Advanced App Catalog": "AppCatalog",
};
const generateDialogReadyChannel = (id) => `Dialog.${id}.ready`;
export class DialogManagerClientFinsembleImpl {
    constructor(gluePromise, asyncConstructors, router, appsClient, distributedStoreClient, loggerClient) {
        this.gluePromise = gluePromise;
        this.asyncConstructors = asyncConstructors;
        this.router = router;
        this.appsClient = appsClient;
        this.distributedStoreClient = distributedStoreClient;
        this.loggerClient = loggerClient;
        const pw = asyncConstructors.addConstructor();
        gluePromise.then((g) => {
            this.glue = g;
            asyncConstructors.finishConstructor(pw);
        });
    }
    logStatus(method, type, data) {
        logStatus(this.loggerClient, this.glue, {
            api: "DialogManagerClient",
            method, type, data
        });
    }
    async hideModal() {
        return null;
    }
    async showModal() {
        return null;
    }
    async getControllerInstance() {
        const controller = globalThis.DialogManagerClient ?? new DialogManagerClientController(this.glue, this.distributedStoreClient);
        globalThis.DialogManagerClient = controller;
        await controller.initializeOnce();
        return controller;
    }
    async sendQueryToDialogInner(routerClient, appsClient, identifier, options, onUserInput) {
        const warn = (timeout) => {
            console.warn(`No response from dialog ${identifier.windowName} after ${timeout / 1000} seconds. Check to make sure your dialog is sending back data.`);
        };
        const controller = await this.getControllerInstance();
        controller.moveDialogFromAvailableToOpened(identifier);
        const queryChannel = `Dialog.${identifier.windowName}.Show`;
        const listenerChannel = generateDialogReadyChannel(identifier.windowName);
        let warningTimeout;
        const unsubscribe = routerClient.addListener(listenerChannel, async (err, response) => {
            if (err) {
                console.log("DialogManagerClient.sendQueryToDialogInner() error from addListener", err);
                return;
            }
            warningTimeout = setTimeout(warn.bind(null, response.data.userInputTimeout), response.data.userInputTimeout);
            const { err: launchError, data } = await appsClient.showWindow(identifier, {
                monitor: options.monitor || "mine",
                left: "center",
                top: "center",
            });
            if (launchError || !data) {
                const error = `DialogManagerClient.sendQueryToDialog error showing window ${identifier.windowName} ${launchError}`;
                console.error(error);
                return;
            }
            const w = await this.glue.windows.findById(identifier.windowName);
            w?.setOnTop(true);
            w?.focus();
            unsubscribe();
        });
        const { err, response } = await routerClient.query(queryChannel, options);
        if (err) {
            console.log(`DialogManagerClient.sendQueryToDialogInner() error contacting ${queryChannel}`, err);
        }
        clearTimeout(warningTimeout);
        controller.moveDialogFromOpenedToAvailable(identifier);
        onUserInput(err, response.data);
    }
    set userInputTimeout(timeout) {
        const controller = globalThis.DialogManagerClient ?? new DialogManagerClientController(this.glue, this.distributedStoreClient);
        controller.userInputTimeout = timeout;
    }
    get openerMessage() {
        const controller = globalThis.DialogManagerClient ?? new DialogManagerClientController(this.glue, this.distributedStoreClient);
        return controller.openerMessage;
    }
    async sendQueryToDialog(identifier, options, onUserInput) {
        await this.asyncConstructors.ready();
        const warn = (timeout) => {
            console.warn(`No response from dialog ${identifier.windowName} after ${timeout / 1000} seconds. Check to make sure your dialog is sending back data.`);
        };
        const controller = await this.getControllerInstance();
        controller.moveDialogFromAvailableToOpened(identifier);
        const queryChannel = `Dialog.${identifier.windowName}.Show`;
        const listenerChannel = generateDialogReadyChannel(identifier.windowName);
        let warningTimeout;
        const unsubscribe = this.router.addListener(listenerChannel, async (err, response) => {
            warningTimeout = setTimeout(warn.bind(null, response.data.userInputTimeout), response.data.userInputTimeout);
            const { err: launchError, data } = await this.appsClient.showWindow(identifier, {
                monitor: options.monitor || "mine",
                left: "center",
                top: "center",
            });
            if (launchError || !data) {
                const error = `DialogManagerClient.sendQueryToDialog() error showing window ${identifier.windowName} ${launchError}`;
                console.log(error);
                return;
            }
            const w = await this.glue.windows.findById(identifier.windowName);
            w?.setOnTop(true);
            w?.focus();
            unsubscribe();
        });
        const { err, response } = await this.router.query(queryChannel, options);
        if (err) {
            console.log(`DialogManagerClient.sendQueryToDialog() error contacting ${queryChannel}`, err);
        }
        clearTimeout(warningTimeout);
        controller.moveDialogFromOpenedToAvailable(identifier);
        onUserInput(err, response.data);
    }
    async spawnDialog(params, inputParams, dialogResponseCallback, cb) {
        this.logStatus("spawnDialog", "start", { params, inputParams });
        await this.asyncConstructors.ready();
        const responseChannel = `DialogChannel-${uuid()}`;
        params.data = { inputParams, responseChannel };
        if (!params.left && params.left !== 0 && !params.right && params.right !== 0) {
            params.left = "center";
        }
        if (!params.top && params.top !== 0 && !params.bottom && params.bottom !== 0) {
            params.top = "center";
        }
        const unsubscribe = this.router.addListener(responseChannel, (err, response) => {
            dialogResponseCallback(err, response.data);
            unsubscribe();
        });
        if (typeof params.url === "string") {
            const gdDisplayIndex = await getGdDisplayIndexWithMine(this.glue, params?.monitor);
            const placement = getPlacementSettings(this.glue, params, gdDisplayIndex);
            if (placement) {
                await adjustPlacementBounds(this.glue, placement, gdDisplayIndex);
            }
            const windowOptions = {
                placement,
                mode: "flat",
                ignoreFromLayouts: true
            };
            await this.glue.windows.open(params.name ?? "", params.url, windowOptions);
            this.logStatus("spawnDialog", "return", { err: null });
            cb?.(null);
            return {
                err: null
            };
        }
        const { error: err, response: data } = await this.appsClient.spawn("dialogTemplate", params);
        if (err) {
            dialogResponseCallback(err, data);
        }
        this.logStatus("spawnDialog", "return", { err, data });
        cb?.(err, data);
        return { err, data };
    }
    async open(type, options, onUserInput) {
        this.logStatus("open", "start", { type, options, onUserInput });
        await this.asyncConstructors.ready();
        type = legacyUINameMap[type] ?? type;
        const openDialog = () => new Promise(async (resolve) => {
            const controller = await this.getControllerInstance();
            controller.getAvailableDialog(type, (dialogIdentifier) => {
                if (dialogIdentifier) {
                    this.sendQueryToDialogInner(this.router, this.appsClient, dialogIdentifier, options, onUserInput);
                    resolve(true);
                }
                else {
                    resolve(false);
                }
            });
        });
        const opened = await openDialog();
        if (opened) {
            this.logStatus("open", "return");
            return null;
        }
        const spawnOptionsForDialogs = {
            options: {
                customData: {
                    foreign: {
                        clients: {
                            dialogManager: {
                                isDialog: true,
                            },
                        },
                    },
                },
            },
        };
        const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
        const { error } = await this.appsClient.spawn(type, spawnOptionsForDialogs);
        if (error) {
            this.logStatus("open", "error", error);
            return `DialogManagerClient.open launching dialog "${type}" error ${error}`;
        }
        else {
            const timeout = 30000;
            for (let i = 0; i < 15; i++) {
                if (await openDialog()) {
                    this.logStatus("open", "return", null);
                    return null;
                }
                else {
                    await sleep(timeout / 15);
                }
            }
            const err = `DialogManagerClient.open error opening dialog "${type}. The dialog launched but didn't register within ${timeout} ms`;
            this.logStatus("open", "error", err);
            return err;
        }
    }
    async getParametersFromInDialog() {
        this.logStatus("getParametersFromInDialog", "start");
        await this.asyncConstructors.ready();
        const controller = await this.getControllerInstance();
        const { inputParams } = controller.spawnData;
        this.logStatus("getParametersFromInDialog", "return", { inputParams });
        return inputParams;
    }
    async respondAndExitFromInDialog(responseParameters) {
        this.logStatus("respondAndExitFromInDialog", "start", responseParameters);
        await this.asyncConstructors.ready();
        const controller = await this.getControllerInstance();
        const { responseChannel } = controller.spawnData;
        this.router.transmit(responseChannel, responseParameters);
        this.logStatus("respondAndExitFromInDialog", "return");
    }
    async showDialog() {
        this.logStatus("showDialog", "start");
        await this.asyncConstructors.ready();
        const controller = await this.getControllerInstance();
        const w = this.glue.windows.my();
        if (!w) {
            console.log("Use of showDialog in incompatible window type");
            return;
        }
        const listenerChannel = generateDialogReadyChannel(w.id);
        this.router.transmit(listenerChannel, {
            userInputTimeout: controller.userInputTimeout ?? 10000,
        });
        this.logStatus("showDialog", "return");
    }
    async registerDialogCallback(cb) {
        this.logStatus("registerDialogCallback", "start");
        await this.asyncConstructors.ready();
        const controller = await this.getControllerInstance();
        if (controller.RESPONDER_CHANNEL) {
            this.router.addResponder(controller.RESPONDER_CHANNEL, (err, message) => {
                if (message) {
                    controller.openerMessage = message;
                }
                cb?.(err, message);
            });
        }
        this.logStatus("registerDialogCallback", "return");
    }
    async respondToOpener(data) {
        this.logStatus("respondToOpener", "start", data);
        await this.asyncConstructors.ready();
        const controller = await this.getControllerInstance();
        controller.openerMessage?.sendQueryResponse(null, data);
        const w = this.glue.windows.my();
        w?.hide();
        controller.openerMessage = null;
        this.logStatus("respondToOpener", "return");
    }
}
